import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createRoutesFromElements, Outlet, Route, RouterProvider, useNavigate } from 'react-router';
import { createBrowserRouter, useLocation } from 'react-router-dom';

import CssBaseline from '@mui/material/CssBaseline';
import ThemeProvider from '@mui/material/styles/ThemeProvider';

import { LightTheme, DarkTheme } from './themes/MainTheme';
import SecureRouteWrapper from './components/SecureRouteWrapper';
import CustomApplicationContext from './util/CustomApplicationContext';
import Login from './pages/Login/Login';
import Logout from './pages/Login/Logout';
import ImpersonateLogin from './pages/Login/ImpersonateLogin';
import Home from './pages/Home';
import './App.css';
import { LicenseInfo } from '@mui/x-license';
import { ExtendedUser } from './common/models/Configuration/User';
import { GetMyUser } from './services/UsersService';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import EmailReceiverPage from 'pages/Anonymous/EmailReceiver';
import moment from 'moment';
import KioskDashboardPage from 'pages/Reporting/Dashboard/KioskDashboard';
import { KindeProvider } from '@kinde-oss/kinde-auth-react';

export default function App() {
  const storedTheme = localStorage.getItem('Theme');
  const [theme, setTheme] = useState<string | null>(storedTheme);
  const [currentUser, setCurrentUser] = useState<ExtendedUser | null>(null);

  const currentLocaleString = useMemo(() => navigator.language, []);

  useEffect(() => {
    moment.updateLocale(currentLocaleString, {
      week: {
        dow: 1,
      },
    });
  }, [currentLocaleString]);

  const handleUserRefresh = useCallback(async () => {
    const res = await GetMyUser();
    if (res) {
      setCurrentUser(res);

      if (process.env.NODE_ENV !== 'development') {
        if (res.userName.indexOf('recruitwizard.com') === -1) {
          window.pendo.initialize({
            visitor: {
              id: res.userName,
              email: res.userName,
              full_name: res.displayName,
              created_date: res.createdDateUnix,
            },

            account: {
              id: res.customerId,
              name: res.customerName,
            },
          });
        }
      }
    }

    setTheme(localStorage.getItem('Theme'));
  }, []);

  const themeObj = useMemo(() => {
    if (theme === 'light') return LightTheme;
    if (theme === 'dark') return DarkTheme;
    return LightTheme;
  }, [theme]);

  const router = useMemo(() => {
    return createBrowserRouter(
      createRoutesFromElements(
        <Route path="/" element={<Layout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route
            path="/impersonate/"
            element={<ImpersonateLogin refreshCurrentUserHandler={handleUserRefresh} />}
          />
          <Route path="/email-receiver/:key" element={<EmailReceiverPage />} />
          <Route path="/kiosk-dashboard/:token" element={<KioskDashboardPage />} />
          <Route
            path="/*"
            element={
              <SecureRouteWrapper>
                <Home />
              </SecureRouteWrapper>
            }
          />
        </Route>
      )
    );
  }, [handleUserRefresh]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <CustomApplicationContext.Provider
        value={{
          currentUser: currentUser,
          refreshCurrentUser: handleUserRefresh,
        }}
      >
        <ThemeProvider theme={themeObj}>
          <CssBaseline />
          <KindeProvider
            domain={window.env.REACT_APP_KINDE_DOMAIN as string}
            clientId={window.env.REACT_APP_KINDE_CLIENT_ID as string}
            redirectUri={`${window.location.origin}/login`}
            logoutUri={`${window.location.origin}/logout`}
          >
            <RouterProvider router={router} />
          </KindeProvider>
        </ThemeProvider>
      </CustomApplicationContext.Provider>
    </LocalizationProvider>
  );
}

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === '/') navigate('/home');
  }, [location.pathname, navigate]);

  LicenseInfo.setLicenseKey(
    'ac3cf85e846c4a97595330be35a9d9eeTz05ODcxNyxFPTE3NTg3NjgxMjIwMDAsUz1wcmVtaXVtLExNPXN1YnNjcmlwdGlvbixQVj1pbml0aWFsLEtWPTI='
  );

  return <Outlet />;
}
